/**
 * 모바일 전용 재고관리 페이지 (PWA Phase 2).
 *
 * 진입 경로: /m/inventory  (PWA start_url)
 * 디자인 원칙:
 *   - 사이드바/햄버거 없음 (네이티브 앱 느낌)
 *   - 카드 리스트 (테이블 X)
 *   - 큰 글자 / 큰 터치 영역 (44px+)
 *   - 검색 + 상단 sticky / 액션 하단 fixed
 *   - viewer 권한도 조회 가능, 출고검수 승인은 mgmt+admin 만
 *
 * 기존 코드 재사용:
 *   - stockApi.listStock() / stock-release-api.getPendingCount()
 *   - StockReleaseFormModal / StockReleaseReviewModal / LeaseManagementModal (lazy)
 *   - useAuth() 권한 분기
 */

import { useState, useMemo, useEffect, Suspense, lazy } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import * as stockApi from '../lib/stock-api';
import * as releaseApi from '../lib/stock-release-api';
import * as pushApi from '../lib/push-api';
import * as checkApi from '../lib/stock-check-api';
import * as auditApi from '../lib/stock-audit-api';
import type { StockRow } from '../lib/types';
import { MobileAppTabs } from '../components/MobileAppTabs';
import { LoadingLines } from '../components/ui/Skeleton';

const StockReleaseFormModal = lazy(() =>
  import('../components/StockReleaseFormModal').then((m) => ({ default: m.StockReleaseFormModal })),
);
const StockReleaseReviewModal = lazy(() =>
  import('../components/StockReleaseReviewModal').then((m) => ({ default: m.StockReleaseReviewModal })),
);
const LeaseManagementModal = lazy(() =>
  import('../components/LeaseManagementModal').then((m) => ({ default: m.LeaseManagementModal })),
);
const DeliveryConfirmationScanModal = lazy(() =>
  import('../components/DeliveryConfirmationScanModal').then((m) => ({ default: m.DeliveryConfirmationScanModal })),
);

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-';
  return n.toLocaleString('ko-KR');
}

/**
 * 안전재고 대비 가용재고 상태 분류
 * - critical: available <= 0
 * - low:      available > 0 && available < safe
 * - ok:       그 외
 */
function urgency(row: StockRow): 'critical' | 'low' | 'ok' {
  const avail = row.availableStock ?? 0;
  const safe = row.safeStock ?? 0;
  if (avail <= 0) return 'critical';
  if (safe > 0 && avail < safe) return 'low';
  return 'ok';
}

/**
 * 📦 이번 주 주요재고 확인 띠 (v2.198.0 → 2차 v2.202.0).
 *
 * ⭐**서버가 내려 준 단계(stage)로만 그린다** — 화면이 「다 셌나」를 따로 계산하지 않는다.
 *   예전엔 「확인 수 ≥ 전체」면 초록이라 **조치 필요가 남아도 「완료」로 보였다**.
 *     checking 파랑(세는 중) · fixing 주황(조치 필요) · ready 연초록(현황표 보낼 차례) · sent 초록(끝)
 * ⚠배포 도중 옛 서버(stage 없음)면 옛 셈법으로 그린다.
 * ⚠불러오기에 실패하거나 품목이 없으면 **아무것도 그리지 않는다** — 재고 목록을 보러 온 사람의
 *   화면에 오류 띠를 얹지 않는다.
 */
function StockCheckBanner() {
  const q = useQuery({
    queryKey: ['stock-check'],
    queryFn: () => checkApi.getWeek(),
    staleTime: 60_000,
  });
  const d = q.data;
  const s = d?.summary;
  if (!d || !s || s.total === 0) return null;
  const checked = d.gate ? d.gate.checked : s.checked;
  const total = d.gate ? d.gate.total : s.total;
  const open = d.gate ? d.gate.open : s.diff;
  const stage = d.gate && d.stage ? d.stage : null;

  let tone: string;
  let titleClass: string;
  let sideClass: string;
  let title: string;
  let side: string;
  let bar = false;
  if (stage === 'fixing') {
    tone = 'bg-amber-500 text-white';
    titleClass = 'text-white';
    sideClass = 'text-amber-50';
    title = `⚠ 주요재고 조치 필요 ${open}건`;
    side = `${checked} / ${total} →`;
  } else if (stage === 'ready') {
    tone = 'bg-lime-50 border border-lime-300';
    titleClass = 'text-lime-900';
    sideClass = 'text-lime-800';
    // 조회전용은 표를 보낼 수 없으니 「보내 주세요」라고 하지 않는다
    title = d.canEdit ? '✅ 확인 완료 · 📊 현황표를 보내 주세요' : '✅ 확인 완료';
    side = d.canEdit ? '→' : `${checked} / ${total}`;
  } else if (stage === 'sent') {
    tone = 'bg-emerald-50 border border-emerald-200';
    titleClass = 'text-emerald-800';
    sideClass = 'text-emerald-700';
    title = '✅ 이번 주 주요재고 확인 완료';
    side = `${checked} / ${total}`;
  } else if (stage === 'checking' || checked < total) {
    tone = 'bg-blue-600 text-white';
    titleClass = 'text-white';
    sideClass = 'text-blue-100';
    title = '📋 이번 주 주요재고 확인';
    side = `${checked} / ${total}${stage === null && open > 0 ? ` · 다름 ${open}` : ''} →`;
    bar = true;
  } else {
    // 옛 서버 · 다 셈
    tone = 'bg-emerald-50 border border-emerald-200';
    titleClass = 'text-emerald-800';
    sideClass = 'text-emerald-700';
    title = '✅ 이번 주 주요재고 확인 완료';
    side = `${checked} / ${total}${open > 0 ? ` · 다름 ${open}` : ''}`;
  }

  return (
    <Link to="/m/stock-check" className={`block rounded px-3 py-2.5 mb-3 shadow-sm active:opacity-80 ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm font-semibold ${titleClass}`}>{title}</span>
        <span className={`text-xs whitespace-nowrap ${sideClass}`}>{side}</span>
      </div>
      {bar && (
        <div className="mt-1 h-1.5 bg-blue-400/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-white"
            style={{ width: `${total ? Math.min(100, (checked / total) * 100) : 0}%` }}
          />
        </div>
      )}
    </Link>
  );
}

/**
 * 🗓 분기 재고 실사 줄 (v2.224.0 · 사용자 요청 「SM재고 앱에서 분기재고실사 시작 버튼」).
 * 세는 중인 실사가 있으면 그 진행률, 없으면 가장 최근 실사의 상태(보고서 결재 중·완료) 또는 「시작」 안내.
 * ⚠불러오기에 실패하면 아무것도 그리지 않는다(주간 확인 띠와 같은 원칙).
 */
function StockAuditBanner() {
  const q = useQuery({
    queryKey: ['stock-audit', 'list', null],
    queryFn: () => auditApi.listAudits(),
    staleTime: 60_000,
  });
  const d = q.data;
  if (!d) return null;
  const a = d.counting ?? d.latest;
  let tone = 'bg-white border border-slate-300';
  let title = '🗓 분기 재고 실사';
  let side = d.canEdit ? '시작하기 →' : '→';
  let bar = 0;
  if (a && a.state === 'counting') {
    tone = 'bg-indigo-600 text-white';
    // 🔎 수시 실물확인이 세는 중이면 그 이름으로 (v2.324.0) — 분기 실사는 종전 그대로 「🗓 2026년 3분기 실사 세는 중」
    title = `${auditApi.isAdhoc(a) ? '🔎' : '🗓'} ${auditApi.headingOf(a)} 세는 중`;
    side = `${a.progress.decided} / ${a.progress.total} →`;
    bar = a.progress.total ? Math.min(100, (a.progress.decided / a.progress.total) * 100) : 0;
  } else if (a && a.state === 'counted') {
    tone = 'bg-lime-50 border border-lime-300';
    title = `🗓 ${a.label} 실사 확인 끝 · 보고서 전`;
    side = '→';
  } else if (a && a.state === 'reviewing') {
    tone = 'bg-amber-50 border border-amber-300';
    title = `🗓 ${a.label} 실사 보고서 결재 중`;
    side = a.report ? a.report.status : '→';
  } else if (a && a.state === 'closed') {
    tone = 'bg-emerald-50 border border-emerald-200';
    title = `🗓 ${a.label} 실사 완료`;
    side = `다음 분기 ${d.current.label === a.label ? '' : '→'}`.trim() || '→';
  }
  const dark = tone.includes('text-white');
  return (
    <>
    <Link to="/m/stock-audit" className={`block rounded px-3 py-2.5 mb-1.5 shadow-sm active:opacity-80 ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm font-semibold ${dark ? 'text-white' : 'text-slate-800'}`}>{title}</span>
        <span className={`text-xs whitespace-nowrap ${dark ? 'text-indigo-100' : 'text-slate-600'}`}>{side}</span>
      </div>
      {bar > 0 && (
        <div className="mt-1 h-1.5 bg-indigo-400/50 rounded-full overflow-hidden">
          <div className="h-full bg-white" style={{ width: `${bar}%` }} />
        </div>
      )}
    </Link>
    {/* 🔎 수시 실물확인 시작 (v2.324.0 · #31) — 세는 중인 실사·확인이 없을 때만 · 누르면 시작 칸이 열린 채로 */}
    {d.canEdit && !d.counting && (
      <Link to="/m/stock-audit?new=adhoc#adhoc" className="block rounded px-3 py-2 mb-3 bg-white border border-dashed border-emerald-400 text-sm font-semibold text-emerald-800 active:bg-emerald-50">
        🔎 수시 실물확인 시작 <span className="text-xs font-normal text-slate-500">— 연휴 전·연말 등 골라서 세기</span>
      </Link>
    )}
    {(!d.canEdit || d.counting) && <div className="mb-1.5" />}
    </>
  );
}

export function MobileInventory() {
  const { user, signOut } = useAuth();

  const [q, setQ] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [managingLease, setManagingLease] = useState(false);
  const [scanUpload, setScanUpload] = useState(false);

  // 푸시 알림 상태 — 'unknown'(초기 확인 중) / 'unsupported' / 'disabled-server' /
  //                'denied' / 'subscribed' / 'unsubscribed'
  const [pushState, setPushState] = useState<
    'unknown' | 'unsupported' | 'disabled-server' | 'denied' | 'subscribed' | 'unsubscribed'
  >('unknown');
  const [pushBusy, setPushBusy] = useState(false);
  const [vapidKey, setVapidKey] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    async function init() {
      if (!pushApi.isPushSupported()) {
        if (mounted) setPushState('unsupported');
        return;
      }
      try {
        const status = await pushApi.getVapidPublicKey();
        if (!mounted) return;
        if (!status.enabled || !status.publicKey) {
          setPushState('disabled-server');
          return;
        }
        setVapidKey(status.publicKey);
        if (Notification.permission === 'denied') {
          setPushState('denied');
          return;
        }
        const sub = await pushApi.currentSubscription();
        if (mounted) setPushState(sub ? 'subscribed' : 'unsubscribed');
      } catch {
        if (mounted) setPushState('unsubscribed');
      }
    }
    void init();
    return () => { mounted = false; };
  }, []);

  async function togglePush() {
    if (pushBusy) return;
    if (pushState === 'subscribed') {
      setPushBusy(true);
      try {
        await pushApi.unsubscribe();
        setPushState('unsubscribed');
      } catch (e) {
        window.alert(`알림 해제 실패: ${(e as Error).message}`);
      } finally {
        setPushBusy(false);
      }
      return;
    }
    if (pushState === 'unsubscribed' && vapidKey) {
      setPushBusy(true);
      try {
        const perm = await pushApi.ensurePermission();
        if (perm !== 'granted') {
          setPushState(perm === 'denied' ? 'denied' : 'unsubscribed');
          return;
        }
        await pushApi.subscribe(vapidKey);
        setPushState('subscribed');
      } catch (e) {
        window.alert(`알림 등록 실패: ${(e as Error).message}`);
      } finally {
        setPushBusy(false);
      }
      return;
    }
    if (pushState === 'denied') {
      window.alert(
        '알림 권한이 차단되어 있습니다.\n브라우저 설정 → 사이트 권한 → 알림에서 허용하세요.',
      );
    }
  }

  const pushIcon =
    pushState === 'subscribed' ? '🔔' :
    pushState === 'denied' ? '🔕' :
    pushState === 'unsupported' || pushState === 'disabled-server' ? '⚪' :
    '🔇';
  const pushTitle =
    pushState === 'subscribed' ? '알림 켜짐 — 탭하면 끕니다' :
    pushState === 'denied' ? '브라우저 알림 권한 차단됨' :
    pushState === 'unsupported' ? '이 브라우저는 알림 미지원' :
    pushState === 'disabled-server' ? '서버 알림 기능 비활성' :
    pushState === 'unsubscribed' ? '알림 꺼짐 — 탭하면 켭니다' :
    '확인 중';

  const stockQ = useQuery({
    queryKey: ['stock-mobile'],
    queryFn: () => stockApi.listStock(),
    staleTime: 30_000,
  });

  // 출고검수 미승인 카운트 — 모든 권한자에게 표시 (실제 승인은 모달 내부에서 sales/admin 만)
  const pendingQ = useQuery({
    queryKey: ['stock-release-pending-mobile'],
    queryFn: () => releaseApi.getPendingCount(),
    staleTime: 30_000,
  });
  const pendingCount = pendingQ.data?.pending ?? 0;

  const filtered = useMemo(() => {
    // 주요재고만 표시 (PC StockStatusTab 과 동일 — isKeyStock=true 만)
    const rows = (stockQ.data ?? []).filter((r) => r.isKeyStock);
    if (!q.trim()) return rows;
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      const name = (r.itemName ?? '').toLowerCase();
      const code = (r.itemCode ?? '').toLowerCase();
      const grp = (r.itemGroup ?? '').toLowerCase();
      return name.includes(term) || code.includes(term) || grp.includes(term);
    });
  }, [stockQ.data, q]);

  // 그룹별 분류 (주요재고 표시 그룹 기준). HDD 그룹을 항상 맨 앞으로.
  const byGroup = useMemo(() => {
    const map = new Map<string, StockRow[]>();
    for (const r of filtered) {
      const g = r.displayGroup ?? r.itemGroup ?? '기타';
      const arr = map.get(g);
      if (arr) arr.push(r);
      else map.set(g, [r]);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === 'HDD') return -1;
      if (b === 'HDD') return 1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* 상단 헤더 */}
      <header className="sticky top-0 z-20 bg-slate-900 text-white px-4 py-3 shadow">
        <div className="flex items-center justify-between">
          {/* ⭐제목 자리를 **두 칸 전환**으로 바꿨다 (v2.188.0). 줄을 새로 넣지 않으려는 것이다 —
              이 헤더는 `sticky top-0` 이고 바로 아래 검색칸이 `top-[52px]` 로 물려 있어서,
              줄을 하나 끼우면 그 오프셋이 전부 어긋난다. 제목은 화면 낭독기용으로만 남긴다. */}
          <h1 className="sr-only">재고관리</h1>
          <MobileAppTabs />
          <div className="flex items-center gap-2 text-xs">
            {/* 데스크탑에서 사이드바 「재고 실사 (모바일)」로 들어온 사람의 돌아갈 길 — 폰(PWA)에서는 숨긴다 (v2.128.0).
                전에는 브라우저 뒤로가기 말고는 포털로 돌아갈 방법이 없었다. */}
            <Link
              to="/stock"
              className="hidden md:inline-flex px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-[11px]"
              title="SM 포털 재고관리로 돌아가기"
            >
              ← SM 포털
            </Link>
            <button
              type="button"
              onClick={togglePush}
              disabled={pushBusy || pushState === 'unknown' || pushState === 'unsupported' || pushState === 'disabled-server'}
              title={pushTitle}
              className="px-2 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded text-base leading-none"
              aria-label={pushTitle}
            >
              {pushBusy ? '⏳' : pushIcon}
            </button>
            <span className="text-slate-300">{user?.name ?? user?.userId ?? ''}</span>
            <button
              type="button"
              onClick={() => signOut()}
              className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-[11px]"
              title="로그아웃"
            >
              로그아웃
            </button>
          </div>
        </div>
      </header>

      {/* 검색 — sticky */}
      <div className="sticky top-[52px] z-10 bg-white border-b border-slate-200 px-3 py-2">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="🔍 품명 / 품목 / 그룹 검색"
          className="w-full border border-slate-300 rounded px-3 py-2.5 text-base focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* 본문 — 카드 리스트 */}
      <main className="flex-1 overflow-y-auto px-3 py-3 pb-24">
        {/* 📦 이번 주 주요재고 확인 (v2.198.0) — 메신저 링크 말고도 들어올 길을 둔다.
            ⚠검색 중에는 숨긴다(찾던 품목 위에 띠가 끼어들면 거슬린다). */}
        {!q.trim() && <StockCheckBanner />}
        {/* 🗓 분기 재고 실사 (v2.224.0) — 주간 확인 띠 아래 */}
        {!q.trim() && <StockAuditBanner />}
        {stockQ.isLoading && (
          <div className="text-center text-slate-400 py-12"><LoadingLines /></div>
        )}
        {stockQ.isError && (
          <div className="text-center text-red-500 py-12 text-sm">
            오류: {(stockQ.error as Error).message}
          </div>
        )}
        {!stockQ.isLoading && filtered.length === 0 && (
          <div className="text-center text-slate-400 py-12 text-sm">
            {q.trim() ? '검색 결과가 없습니다.' : '재고 데이터가 없습니다.'}
          </div>
        )}
        {byGroup.map(([group, rows]) => (
          <section key={group} className="mb-4">
            <h2 className="text-xs font-semibold text-slate-500 px-1 py-1 sticky top-0">
              {group} <span className="text-slate-400 font-normal">({rows.length})</span>
            </h2>
            <ul className="space-y-2">
              {rows.map((r) => {
                const lv = urgency(r);
                const border =
                  lv === 'critical' ? 'border-l-4 border-red-500' :
                  lv === 'low' ? 'border-l-4 border-amber-400' :
                  'border-l-4 border-slate-200';
                const availColor =
                  lv === 'critical' ? 'text-red-600 font-bold' :
                  lv === 'low' ? 'text-amber-600 font-semibold' :
                  'text-slate-800';
                return (
                  <li
                    key={r.itemCode}
                    className={`bg-white rounded shadow-sm ${border} px-3 py-2.5`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-900 truncate">
                          {r.itemName ?? r.itemCode}
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono truncate">
                          {r.itemCode}
                        </div>
                      </div>
                      {lv === 'critical' && (
                        <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
                          재고부족
                        </span>
                      )}
                      {lv === 'low' && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
                          안전재고미달
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-4 gap-1 text-center text-xs">
                      <Stat label="현재고" value={fmt(r.currentStock)} highlight />
                      <Stat label="입고예정" value={fmt(r.incoming)} />
                      <Stat label="납품예정" value={fmt(r.pendingDelivery)} />
                      <Stat label="가용" value={fmt(r.availableStock)} valueClass={availColor} />
                    </div>
                    {(r.safeStock ?? 0) > 0 && (
                      <div className="mt-1 text-[10px] text-slate-400 text-right">
                        안전재고: {fmt(r.safeStock)}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </main>

      {/* 하단 고정 액션 바 — ⭐네 칸 같은 너비 · 칸마다 그림 + 이름 (v2.225.0 · 사용자: 「출고등록·출고검수만 너무 크고
          옆의 두 개는 너무 작아서 뭔지도 안 보여」). 전에는 앞 둘이 flex-1, 뒤 둘(🔄 임대 · 🖊 납품확인증)은 그림만 있었다.
          ⚠칸 높이가 늘었다(약 60px) — 본문 pb-24 가 가려지지 않게 받친다. */}
      <div
        className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-slate-200 px-2 py-2 shadow-lg"
        style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
      >
        <div className="grid grid-cols-4 gap-1.5">
          <ActionButton icon="📦" label="출고등록" tone="bg-blue-600 hover:bg-blue-700 active:bg-blue-800" onClick={() => setReleasing(true)} />
          {/* 검수/임대 버튼 — 모든 권한자에게 노출. 승인/처리 자체는 모달 내부에서 mgmt/admin 분기. */}
          <ActionButton icon="✓" label="출고검수" tone="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800" onClick={() => setReviewing(true)} badge={pendingCount} />
          <ActionButton icon="🔄" label="임대관리" title="임대품목 관리" tone="bg-slate-600 hover:bg-slate-700 active:bg-slate-800" onClick={() => setManagingLease(true)} />
          {/* 납품확인증 스캔본 등록 — 전 직원 사용 가능. 현장에서 휴대폰 촬영으로 바로 첨부 */}
          <ActionButton icon="🖊" label="납품확인증" title="납품확인증 스캔본 등록" tone="bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800" onClick={() => setScanUpload(true)} />
        </div>
      </div>

      {/* lazy 모달 */}
      <Suspense fallback={null}>
        {releasing && (
          <StockReleaseFormModal
            mode="create"
            onClose={() => setReleasing(false)}
            onSaved={() => setReleasing(false)}
          />
        )}
        {reviewing && (
          <StockReleaseReviewModal onClose={() => setReviewing(false)} />
        )}
        {managingLease && (
          <LeaseManagementModal onClose={() => setManagingLease(false)} />
        )}
        {scanUpload && (
          <DeliveryConfirmationScanModal onClose={() => setScanUpload(false)} />
        )}
      </Suspense>
    </div>
  );
}

/** 아래 단추 줄 한 칸 — 그림 위 · 이름 아래 (v2.225.0) */
function ActionButton({
  icon,
  label,
  title,
  tone,
  onClick,
  badge = 0,
}: {
  icon: string;
  label: string;
  title?: string;
  /** 바탕색 (hover·active 포함) */
  tone: string;
  onClick: () => void;
  /** 오른쪽 위 빨간 숫자(출고검수 대기) — 0 이면 안 그린다 */
  badge?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title ?? label}
      className={`relative ${tone} text-white min-h-[56px] px-1 py-1.5 rounded shadow flex flex-col items-center justify-center gap-0.5`}
    >
      <span className="text-lg leading-none" aria-hidden="true">{icon}</span>
      <span className="text-xs font-semibold leading-tight whitespace-nowrap">{label}</span>
      {badge > 0 && (
        <span className="absolute -top-1.5 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1.5">
          {badge}
        </span>
      )}
    </button>
  );
}

function Stat({
  label,
  value,
  valueClass,
  highlight,
}: {
  label: string;
  value: string;
  valueClass?: string;
  /** 살짝 강조 (현재고 등 핵심 수치) — 옅은 파란 배경 + 진한 글씨 */
  highlight?: boolean;
}) {
  const bg = highlight ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50';
  const labelClass = highlight ? 'text-slate-600 font-medium' : 'text-slate-500';
  const defaultValueClass = highlight ? 'text-slate-900 font-bold' : 'text-slate-800';
  return (
    <div className={`${bg} rounded py-1.5`}>
      <div className={`text-[10px] ${labelClass}`}>{label}</div>
      <div className={`text-sm font-mono ${valueClass ?? defaultValueClass}`}>{value}</div>
    </div>
  );
}
